import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHammer,
  faCheckCircle,
  faLayerGroup,
  faQuestionCircle,
  faChartLine,
  faLock
} from '@fortawesome/free-solid-svg-icons';

const BuildSummary = ({ stats, config, options, onBuildTest, isSubscribed, theme }) => {
  const needsSubscription = !isSubscribed && 
    (config.numberOfQuestions > 5 || config.timeAmount > 0);

  const styles = {
    container: {
      background: theme?.background?.card,
      borderRadius: '1.25rem',
      padding: '1.5rem',
      border: `10px solid ${theme?.primary?.orange?.[500]}`,
      boxShadow: theme?.shadows?.base,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      marginBottom: '1.5rem',
    },
    icon: {
      fontSize: '1.25rem',
      color: theme?.primary?.orange?.[500],
    },
    title: {
      fontSize: '1.125rem',
      fontWeight: '600',
      color: theme?.primary?.gray?.[900],
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    statCard: {
      background: theme?.primary?.gray?.[50],
      padding: '1.25rem',
      borderRadius: '0.75rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    },
    statCardHover: {
      transform: 'translateY(-2px)',
      boxShadow: theme?.shadows?.base,
      borderColor: theme?.primary?.orange?.[300],
    },
    statIcon: {
      fontSize: '1.25rem',
      marginBottom: '0.75rem',
      color: theme?.primary?.orange?.[500],
    },
    statValue: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: theme?.primary?.gray?.[900],
      marginBottom: '0.5rem',
    },
    statLabel: {
      fontSize: '0.75rem',
      color: theme?.primary?.gray?.[600],
      fontWeight: '500',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    configSummary: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    configCard: {
      background: theme?.primary?.gray?.[100],
      padding: '1.25rem',
      borderRadius: '0.75rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    },
    configCardHover: {
      transform: 'translateY(-2px)',
      boxShadow: theme?.shadows?.base,
      borderColor: theme?.primary?.orange?.[300],
    },
    configLabel: {
      fontSize: '0.75rem',
      color: theme?.primary?.gray?.[600],
      fontWeight: '500',
      marginBottom: '0.5rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    configValue: {
      fontSize: '1rem',
      fontWeight: '700',
      color: theme?.primary?.gray?.[900],
    },
    contentWrapper: {
      flex: 1,
    },
    buildButton: {
      width: '100%',
      padding: '1rem',
      borderRadius: '0.75rem',
      border: 'none',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      marginTop: 'auto',
    },
    primaryButton: {
      background: `linear-gradient(135deg, ${theme?.primary?.orange?.[500]} 0%, ${theme?.primary?.orange?.[600]} 100%)`,
      color: 'white',
      boxShadow: `0 4px 12px ${theme?.primary?.orange?.[500]}30`,
    },
    premiumButton: {
      background: `linear-gradient(135deg, ${theme?.primary?.yellow?.[500]} 0%, ${theme?.primary?.yellow?.[600]} 100%)`,
      color: 'white',
      boxShadow: `0 4px 12px ${theme?.primary?.yellow?.[500]}30`,
    },
    buttonHover: {
      transform: 'translateY(-2px)',
      boxShadow: theme?.shadows?.lg,
    },
    warningBox: {
      background: theme?.primary?.yellow?.[100],
      border: `1px solid ${theme?.primary?.yellow?.[300]}`,
      borderRadius: '0.75rem',
      padding: '1rem',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    warningText: {
      fontSize: '0.875rem',
      color: theme?.primary?.yellow?.[700],
      fontWeight: '500',
    },
  };

  const getStatColor = (value, type) => {
    if (type === 'score') {
      if (value >= 70) return theme?.primary?.green?.[500];
      if (value >= 40) return theme?.primary?.yellow?.[500];
      return theme?.primary?.red?.[500];
    }
    return theme?.primary?.orange?.[500];
  };

  return (
    <motion.div
      style={styles.container}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
    >
      <div style={styles.contentWrapper}>
        <div style={styles.header}>
          <FontAwesomeIcon icon={faHammer} style={styles.icon} />
          <h3 style={styles.title}>Test Summary</h3>
        </div>
        
        <div style={styles.configSummary}>
          <motion.div 
            style={styles.configCard}
            whileHover={styles.configCardHover}
            transition={{ duration: 0.2 }}
          >
            <span style={styles.configLabel}>Domains Selected</span>
            <span style={styles.configValue}>
              {stats.domainsSelected}/{stats.totalDomains}
            </span>
          </motion.div>
          <motion.div 
            style={styles.configCard}
            whileHover={styles.configCardHover}
            transition={{ duration: 0.2 }}
          >
            <span style={styles.configLabel}>Test Mode</span>
            <span style={styles.configValue}>
              {config.mode === 'questions' 
                ? `${config.numberOfQuestions} Questions`
                : `${config.timeAmount} Minutes`
              }
            </span>
          </motion.div>
          
          <motion.div 
            style={styles.configCard}
            whileHover={styles.configCardHover}
            transition={{ duration: 0.2 }}
          >
            <span style={styles.configLabel}>Show Answers</span>
            <span style={styles.configValue}>
              {options.showAnswer ? 'Yes' : 'No'}
            </span>
          </motion.div>
          
          <motion.div 
            style={styles.configCard}
            whileHover={styles.configCardHover}
            transition={{ duration: 0.2 }}
          >
            <span style={styles.configLabel}>Priority</span>
            <span style={styles.configValue}>
              {options.priority}
            </span>
          </motion.div>
        </div>
        
        {needsSubscription && (
          <div style={styles.warningBox}>
            <FontAwesomeIcon icon={faLock} style={{ color: theme?.primary?.yellow?.[700] }} />
            <span style={styles.warningText}>
              Premium subscription required for more than 5 questions or timed tests
            </span>
          </div>
        )}
      </div>
      
      <motion.button
        style={{
          ...styles.buildButton, ...styles.primaryButton
        }}
        whileHover={styles.buttonHover}
        whileTap={{ scale: 0.95 }}
        onClick={onBuildTest}
      >
        <FontAwesomeIcon icon={needsSubscription ? faLock : faHammer} />
        {needsSubscription ? 'Get Premium & Continue' : 'Build Custom Test'}
      </motion.button>
    </motion.div>
  );
};

export default BuildSummary;